/**
 * API service for ADM Management Dashboard (Super Admin)
 * Auth: login without company_code. Tenant API: /api/manage/tenants
 * Uses HTTPS when the app is served over HTTPS to avoid mixed-content blocking.
 */

import axios from 'axios';

/**
 * Resolve API base URL. When deployed on HTTPS, use HTTPS for the API to avoid
 * mixed-content blocking (browsers block HTTP requests from HTTPS pages).
 */
function getApiBaseUrl() {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    // If app is on HTTPS but env URL is HTTP, use HTTPS for the same host (server must support it)
    if (typeof window !== 'undefined' && window.location.protocol === 'https:' && envUrl.startsWith('http://')) {
      return envUrl.replace(/^http:\/\//, 'https://');
    }
    return envUrl;
  }
  // Development or same-origin: use current origin + /api when on HTTPS, else default
  if (typeof window !== 'undefined') {
    if (window.location.protocol === 'https:') {
      return `${window.location.origin}/api`;
    }
    return 'https://102.203.200.190:5000/api';
  }
  return 'http://102.203.200.190:5000/api';
}

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.message || error.response?.data?.error || 'An error occurred';
    return Promise.reject(new Error(message));
  }
);

/** Super-admin login: username + password only (no company_code) */
export const authService = {
  login: async (credentials) => {
    const { username, password } = credentials;
    const response = await api.post('/auth/login', { username, password });
    const { token, user } = response.data.data;
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
    }
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  isAuthenticated: () => !!localStorage.getItem('token'),

  getProfile: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

/** Tenant management (super-admin only) */
export const tenantService = {
  list: async () => {
    const response = await api.get('/manage/tenants');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/manage/tenants/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/manage/tenants', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/manage/tenants/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/manage/tenants/${id}`);
    return response.data;
  },

  /** Create a user for a tenant (set first username/password for the company) */
  createUser: async (tenantId, data) => {
    const response = await api.post(`/manage/tenants/${tenantId}/users`, data);
    return response.data;
  },
};
